import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import { useLinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-link-action-mutations";
import type {
  CreateActionRequest,
  CreateFieldRequest,
  PrintPdfConfiguration,
  Workflow,
} from "~/types/api";
import { useWorkflowCreationContext } from "../../../workflow-creation-context-provider";
import { useWorkflowsDialog } from "../../use-workflows-dialog";

type WorkflowsMutationContext = {
  previousWorkflows: Workflow[] | undefined;
};

const missingOutputFieldMessage = "You cannot create an action without an outputField defined.";

const hasInvalidActionOutputField = (
  actions: CreateActionRequest[],
  fields: CreateFieldRequest[]
) => {
  const directoryFieldIds = new Set(
    fields.filter((field) => field.type === "Directory").map((field) => field.id)
  );

  return actions.some((action) => {
    const configuration = action.configuration as PrintPdfConfiguration;
    return !configuration.outputFieldId || !directoryFieldIds.has(configuration.outputFieldId);
  });
};

export const useCreateWorkflow = () => {
  const {
    state: { workflow, fields, statuses, actions, isUpgradeMode },
    resetState,
    onAfterCreate,
  } = useWorkflowCreationContext();
  const { linkStatus } = useLinkActionStatus();

  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const workflowsQueryOptions = configurationApi.queryOptions("get", "/api/Workflow");
  const workflowsQueryKey = workflowsQueryOptions.queryKey;
  const { closeDialog } = useWorkflowsDialog();

  const { mutateAsync: mutateWorkflow, isPending } = configurationApi.useMutation(
    "post",
    "/api/Workflow",
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: workflowsQueryKey });
        const previousWorkflows = queryClient.getQueryData(workflowsQueryKey);

        const optimisticWorkflow = {
          id: `temp-${Date.now()}`,
          ...variables.body,
          fields: fields,
          statuses: statuses,
          procedures: actions,
        };
        queryClient.setQueryData(workflowsQueryKey, (oldData: Workflow[]) => {
          if (Array.isArray(oldData)) {
            return [...oldData, optimisticWorkflow];
          }
          return oldData;
        });

        return { previousWorkflows };
      },
      onError: (_err, _variables, context) => {
        const previousWorkflows = (context as WorkflowsMutationContext)?.previousWorkflows;
        if (previousWorkflows) {
          queryClient.setQueryData(workflowsQueryKey, previousWorkflows);
        }
        const errorMessage = getErrorMessage(_err, "Failed to create workflow");
        showToast("error", errorMessage, "Failed to create workflow.");
      },
      onSuccess: async (data) => {
        const afterCreateFn = onAfterCreate.current;
        if (isUpgradeMode && afterCreateFn && data?.id) {
          await afterCreateFn(data.id);
        }
        if (!isUpgradeMode && data?.id) {
          showToast("success", "Workflow created successfully.", "Success");
        }
        const linkPromises = actions.reduce<Promise<unknown>[]>((promises, action, index) => {
          const actionId = data.procedures?.find((p) => p.name === action.name)?.id;

          if (actionId) {
            (action.statusCodes ?? []).forEach((statusCode) => {
              promises.push(linkStatus(data.id, actionId, statusCode, index));
            });
          }
          return promises;
        }, []);

        await Promise.all(linkPromises);
        resetState();
        closeDialog?.();
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: workflowsQueryKey });
      },
    }
  );

  const handleMutate = async () => {
    if (!workflow?.customerId) return;
    if (hasInvalidActionOutputField(actions, fields)) {
      showToast("error", missingOutputFieldMessage, "Missing output field");
      return;
    }

    await mutateWorkflow({
      body: {
        customerId: workflow?.customerId ?? null,
        name: workflow?.name ?? "",
        previousVersionId: workflow?.previousVersionId ?? null,
        fields,
        statuses,
        procedures: actions.map(({ name, configuration }) => ({ name, configuration })),
      },
    });
  };

  return { handleMutate, isPending };
};
